import { Injectable, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { User } from '@socrates/common';
import { IUserRepo, OrgPublicInfo, ProfileData, ProfilePayload, SignupOutcome, SignupPayload } from '../repository/interfaces/iuser.repo';

@Injectable()
export class UserService {

	private static _user: User;
	private _detouredRoute: string;
	private _loginEmitter: EventEmitter<User> = new EventEmitter<User>();
	private _logoutEmitter: EventEmitter<void> = new EventEmitter<void>();


	constructor(
		private userRepo: IUserRepo,
		private router: Router,
	) {
	}

	logout(): void {
		if (UserService._user) {
			this.userRepo.logout();
			UserService._user = null;
		}
		this._detouredRoute = null;
		this._logoutEmitter.emit();
	}

	async authenticateUser(userCredentials: any): Promise<User> {
		const user = await this.userRepo.loginUser(userCredentials);
		UserService._user = user;
		const account = await this.userRepo.getMyAccount();
		user.trustLevel = account.trustLevel;
		this._loginEmitter.emit(user);
		return user;
	}

	async firstLoginPasswordUpdate(username: string, newPassword: string): Promise<User> {
		const user = await this.userRepo.firstLoginResetPassword(username, newPassword);
		UserService._user = user;
		this._loginEmitter.emit(user);
		return user;
	}

	async resetPassword(username: string): Promise<boolean> {
		return this.userRepo.requestPasswordReset(username);
	}

	async resetPasswordConfirm(username: string, newPassword: string, resetCode: string): Promise<User> {
		const user = await this.userRepo.passwordResetConfirm(username, newPassword, resetCode);
		UserService._user = user;
		this._loginEmitter.emit(user);
		return user;
	}

	getUserFromSession(): User {
		if (!UserService._user) {
			UserService._user = this.userRepo.userFromSession();
			if (UserService._user) {
				this._loginEmitter.emit(UserService._user);
			}
		}
		return UserService._user;
	}

	getUser(): User {
		return UserService._user ?? null;
	}

	async updateOwnPassword(password: string): Promise<boolean> {
		return this.userRepo.updateOwnPassword(password);
	}

	async updateOwnUser(user: User): Promise<User> {
		const updated = await this.userRepo.updateOwnUser(user);
		UserService._user = updated;
		return updated;
	}

	async checkSession(): Promise<User> {
		const user = await this.userRepo.validateSession();
		UserService._user = user;
		const account = await this.userRepo.getMyAccount();
		user.trustLevel = account.trustLevel;
		return user;
	}

	rerouteIfNeeded(): void {
		if (this._detouredRoute) {
			if (!this._detouredRoute.match(/error/i)) {
				const reroute = this._detouredRoute;
				this._detouredRoute = null;
				this.router.navigate([reroute]);
			} else {
				this._detouredRoute = null;
			}
		}
	}

	subscribeToLogins(func: (user: User) => void): void {
		this._loginEmitter.subscribe(func);
	}

	subscribeToLogouts(func: () => void): void {
		this._logoutEmitter.subscribe(func);
	}

	get user(): User {
		return UserService._user;
	}

	get detouredRoute(): string {
		return this._detouredRoute;
	}

	set detouredRoute(newDetouredRoute: string) {
		if (newDetouredRoute !== '/login') {
			this._detouredRoute = newDetouredRoute;
		}
	}

	async listPublicOrgs(): Promise<OrgPublicInfo[]> {
		return this.userRepo.listPublicOrgs();
	}

	async signup(payload: SignupPayload): Promise<SignupOutcome> {
		return this.userRepo.signup(payload);
	}

	async resolveInvite(token: string): Promise<{ orgId: string; orgName: string }> {
		return this.userRepo.resolveInvite(token);
	}

	async getOwnProfile(): Promise<ProfileData> {
		return this.userRepo.getOwnProfile();
	}

	async updateProfile(payload: ProfilePayload): Promise<void> {
		await this.userRepo.updateProfile(payload);
		// Reflect name changes in the cached user
		if (UserService._user) {
			UserService._user.firstName = payload.firstName;
			UserService._user.lastName  = payload.lastName;
		}
	}
}
